import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';

const OTP_COOLDOWN_SECONDS = 60;
const OTP_COOLDOWN_STORAGE_KEY = 'otpCooldownEnd';

const OTPPage = () => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';

  useEffect(() => {
    const getRemainingSeconds = () => {
      const cooldownEnd = Number(sessionStorage.getItem(OTP_COOLDOWN_STORAGE_KEY));

      if (!cooldownEnd) return 0;

      const remainingSeconds = Math.ceil((cooldownEnd - Date.now()) / 1000);

      if (remainingSeconds <= 0) {
        sessionStorage.removeItem(OTP_COOLDOWN_STORAGE_KEY);
        return 0;
      }

      return remainingSeconds;
    };

    setCooldown(getRemainingSeconds());

    const timer = setInterval(() => {
      setCooldown(getRemainingSeconds());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 6) return;
    setLoading(true);

    try {
      // Use direct fetch for OTP verification to avoid SDK body-stream bugs
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/auth/v1/verify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            email,
            token: otp,
            type: 'email',
            gotrue_meta_security: {},
          }),
        }
      );

      if (res.status === 429) {
        toast.error('Too many attempts. Please wait and try again.');
        return;
      }

      if (!res.ok) {
        // Try signup type for first-time users
        const res2 = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/auth/v1/verify`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              email,
              token: otp,
              type: 'signup',
              gotrue_meta_security: {},
            }),
          }
        );

        if (!res2.ok) {
          const errData = await res2.json().catch(() => ({}));
          toast.error(errData.msg || errData.error_description || 'Invalid OTP code. Please try again.');
          return;
        }

        const data2 = await res2.json();
        if (data2.access_token) {
          await supabase.auth.setSession({
            access_token: data2.access_token,
            refresh_token: data2.refresh_token,
          });
          toast.success('Login successful!');
          navigate('/dashboard');
          return;
        }
      }

      const data = await res.json();
      if (data.access_token) {
        await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        toast.error('Verification failed. Please try again.');
      }
    } catch (error) {
      toast.error('Verification failed. Please check your code and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;

    const cooldownEnd = Date.now() + OTP_COOLDOWN_SECONDS * 1000;
    sessionStorage.setItem(OTP_COOLDOWN_STORAGE_KEY, String(cooldownEnd));
    setCooldown(OTP_COOLDOWN_SECONDS);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/auth/v1/otp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            email,
            create_user: true,
            gotrue_meta_security: {},
          }),
        }
      );
      if (res.status === 429) {
        toast.error('Too many requests. Please wait a minute.');
        return;
      }
      if (!res.ok) {
        toast.error('Failed to resend OTP');
        return;
      }
      toast.success('New OTP sent!');
    } catch (error) {
      toast.error('Failed to resend OTP');
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">No email provided. Please login first.</p>
          <Button onClick={() => navigate('/login')} data-testid="go-to-login-btn">Go to Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/7c1de106-ee36-4e18-a9e4-f71f970980ea/images/47bec2839d9882d2ab111193c2b4f3dd83c2fb92f19111c9b303c1f0acf3ec2b.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <h2 className="text-3xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Two-Factor Verification
          </h2>
          <p className="text-lg text-white/80">
            Enter the verification code sent to your email to complete authentication.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-background">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors duration-150"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </button>

          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-10 h-10 text-primary" />
            <h1 className="text-2xl sm:text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="otp-title">
              Verify OTP
            </h1>
          </div>

          <p className="text-sm text-muted-foreground mb-6">
            We sent a verification code to <span className="font-mono text-foreground">{email}</span>
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="otp">Enter OTP Code</Label>
              <Input
                id="otp"
                type="text"
                placeholder="00000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))}
                required
                maxLength={8}
                data-testid="otp-input"
                className="mt-2 text-center text-2xl tracking-widest font-mono"
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading || otp.length < 6} data-testid="otp-submit-btn">
              {loading ? 'Verifying...' : 'Verify & Login'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={handleResend}
              disabled={cooldown > 0}
              className="text-sm text-primary hover:underline disabled:pointer-events-none disabled:opacity-50"
              data-testid="resend-otp-btn"
            >
              Resend OTP Code
            </button>
            {cooldown > 0 && (
              <span className="ml-2 text-xs text-muted-foreground font-mono" data-testid="otp-resend-cooldown">
                Resend available in {cooldown}s...
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPPage;
